import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { createSelector } from 'reselect'
import { v4 } from 'uuid'
import { l } from '@manychat/manyui'

import { ConversationStatus, Message } from 'apps/aiAutomation/components/Playground/types'
import { AiAutomationEngagementGoalSelectors } from 'apps/aiAutomation/store/engagementGoal'
import { AiAutomationPlaygroundApi } from 'shared/api/requests/aiAutomationPlayground'
import { GoalType } from 'shared/api/requests/aiEngagementGoal/schemas'
import { makeCreateAsyncAction } from 'shared/lib/redux'
import { accountNotificationsListener } from 'utils/services/notificationsService'

import { LeadQualPlaygroundSocketEventsSchemas } from './schemas'
import { ConversationEndReason, LeadQualPlaygroundInitialState } from './types'

const createAsyncAction = makeCreateAsyncAction({
  scope: 'ai',
  section: 'ai_always_on_automation',
})

const NAMESPACE = 'leadQualPlayground'

const { reducer, actions } = createSlice({
  initialState: LeadQualPlaygroundInitialState,
  name: NAMESPACE,
  reducers: {
    sessionReset: () => LeadQualPlaygroundInitialState,

    messageAdded: (state, action: PayloadAction<Message>) => {
      state.messages.push(action.payload)
      state.status = ConversationStatus.ONGOING
    },

    typingAnimationSet: (state, action: PayloadAction<Message>) => {
      state.status = ConversationStatus.ONGOING_TYPING
      state.typingMessageBuffer = action.payload
    },

    typingMessageFlushed: (state) => {
      if (state.typingMessageBuffer) {
        state.messages.push(state.typingMessageBuffer)
        state.typingMessageBuffer = null
        state.status = ConversationStatus.ONGOING
      }
    },

    finishReasonSet: (state, action: PayloadAction<ConversationEndReason>) => {
      state.endReason = action.payload
      state.status = ConversationStatus.FINISHED
    },

    sessionIdUpdated: (state, action: PayloadAction<string>) => {
      state.sessionId = action.payload
    },
  },
  extraReducers: (builder) => {
    builder.addCase(startSessionFx.pending, (state, action) => {
      state.status = ConversationStatus.ONGOING_WAITING
      state.goalId = action.meta.arg
    })

    builder.addCase(startSessionFx.rejected, (state) => {
      state.sessionId = null
    })

    builder.addCase(sendMessageFx.pending, (state, action) => {
      state.status = ConversationStatus.ONGOING_WAITING

      state.messages.push({
        type: 'text',
        author: 'user',
        data: {
          text: action.meta.arg.message,
        },
      })
    })
  },
})

const startSessionFx = createAsyncAction(
  `${NAMESPACE}/startSessionFx`,
  async (goalId: string, { dispatch }) => {
    const sessionId = v4()

    dispatch(actions.sessionIdUpdated(sessionId))

    await AiAutomationPlaygroundApi.startSessionByGoal({
      body: {
        goal_id: goalId,
        session_id: sessionId,
      },
    })
  },
)

const sendMessageFx = createAsyncAction(
  `${NAMESPACE}/sendMessageFx`,
  async ({ message }: { message: string }, { getState }) => {
    const sessionId = LeadQualPlaygroundSelectors.getSessionId(getState()) as string

    await AiAutomationPlaygroundApi.sendMessage({
      body: {
        session_id: sessionId,
        user_message: message,
      },
    })
  },
  {
    condition: (_, { getState }) =>
      LeadQualPlaygroundSelectors.getSessionId(getState()) !== null ||
      LeadQualPlaygroundSelectors.getIsOngoingConversation(getState()),
  },
)

accountNotificationsListener.on(
  LeadQualPlaygroundSocketEventsSchemas.messageGenerated,
  (data, dispatch, getState) => {
    if (LeadQualPlaygroundSelectors.getSessionId(getState() as RootState) !== data.session_id) {
      return
    }

    dispatch(actions.typingMessageFlushed())

    dispatch(
      actions.typingAnimationSet({
        type: 'text',
        author: 'ai',
        data: {
          text: data.message.text,
        },
      }),
    )

    setTimeout(() => {
      dispatch(actions.typingMessageFlushed())
    }, 1000)
  },
)

accountNotificationsListener.on(
  LeadQualPlaygroundSocketEventsSchemas.goalFinished,
  (data, dispatch, getState) => {
    if (LeadQualPlaygroundSelectors.getSessionId(getState() as RootState) !== data.session_id) {
      return
    }

    dispatch(actions.typingMessageFlushed())

    const fallbackAssignee = LeadQualPlaygroundSelectors.getFallbackAssignee(
      getState() as RootState,
    )

    const unsuccessfulMessage = fallbackAssignee
      ? l.translate('Set to assign if info isn’t shared.')
      : l.translate('No next step was set if info isn’t shared.')

    dispatch(
      actions.messageAdded({
        type: 'system',
        author: 'ai',
        data: {
          text:
            data.reason === ConversationEndReason.ACHIEVED
              ? l.translate('All info was collected.')
              : unsuccessfulMessage,
        },
      }),
    )

    dispatch(actions.finishReasonSet(data.reason))
  },
)

accountNotificationsListener.on(
  LeadQualPlaygroundSocketEventsSchemas.fieldObtained,
  (data, dispatch, getState) => {
    if (LeadQualPlaygroundSelectors.getSessionId(getState() as RootState) !== data.session_id) {
      return
    }

    dispatch(actions.typingMessageFlushed())

    dispatch(
      actions.messageAdded({
        type: 'fieldObtained',
        author: 'ai',
        data: {
          field_id: data.field_id,
          field_value: data.field_value,
        },
      }),
    )
  },
)

export const LeadQualPlaygroundReducer = reducer

export const LeadQualPlaygroundActions = {
  startSessionFx,
  sendMessageFx,

  sessionReset: actions.sessionReset,
}

const getState = (state: RootState) => state.aiAutomation.leadQualPlayground

const getFallbackAssignee = createSelector(
  [
    (state) => {
      const { goalId } = getState(state)

      return goalId ? AiAutomationEngagementGoalSelectors.getItemById(state, goalId) : null
    },
  ],
  (goal) => {
    if (goal === null || goal.type !== GoalType.LEAD_QUAL) {
      return null
    }

    return (
      goal.data.settings.fallback_assignee?.user_id ??
      goal.data.settings.fallback_assignee?.group_id ??
      null
    )
  },
)

export const LeadQualPlaygroundSelectors = {
  getSessionId: (state: RootState) => getState(state).sessionId,
  getMessages: (state: RootState) => getState(state).messages,
  getConversationStatus: (state: RootState) => getState(state).status,
  getIsOngoingConversation: (state: RootState) =>
    getState(state).status === ConversationStatus.ONGOING,
  getConversationEndReason: (state: RootState) => getState(state).endReason,
  getFallbackAssignee: getFallbackAssignee,
}
